import { TAGESMODI, tagesmodusWaehlen, type Tagesmodus } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { sitzungQuery } from '../../lib/queries'

export { TAGESMODI, type Tagesmodus }

/**
 * **Der Umschalter über „Mein Tag"**
 * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md), seit dem 30.09.2026 vier
 * Ansichten – [Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.12).
 *
 * | Modus | Was er zeigt |
 * |---|---|
 * | `einfach` | drei Zeit-Zonen (Vorlauf → Jetzt → Erledigt) und die Nachrichten daneben |
 * | `ausfuehrlich` | die gewachsene Ansicht aus [Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md) |
 * | `kalender` | der Tag als Zeitstrahl: Termine, Grundlast, Aufgaben in den Lücken |
 * | `circle` | die radiale Schleuse: Arbeitswelt × Zeitfenster, Mitte = in Arbeit |
 *
 * ## Gerät **und** Konto – in dieser Reihenfolge
 *
 * Die Wahl liegt weiter **je Gerät** im `localStorage`. Das war Z-12s Überlegung und sie gilt
 * unverändert: Am großen Bildschirm mag jemand „Ausführlich" wollen und auf dem Telefon
 * „Einfach" – eine reine Kontoeinstellung könnte das nicht unterscheiden.
 *
 * Neu daneben (Nils: *„Jeder User kann sich selber seine «Standard» ansicht dann speichern"*):
 * eine **Standardansicht am Konto**. Sie greift nur, wenn das Gerät nichts weiß – auf einem
 * neuen Rechner, nach dem Leeren des Speichers, im privaten Fenster. Die Regel selbst steht in
 * `tagesmodusWaehlen` in `shared`, damit Server und Oberfläche dieselbe benutzen.
 */
const SCHLUESSEL = 'netcon-work:meintag-modus'

function vomGeraet(): string | null {
  try {
    return window.localStorage.getItem(SCHLUESSEL)
  } catch {
    return null
  }
}

export function useTagesmodus(): [Tagesmodus, (modus: Tagesmodus) => void] {
  const { data: sitzung } = useQuery(sitzungQuery)
  const vomKonto = sitzung?.meintagStandard ?? null

  /**
   * **Der erste Wert kommt ohne die Sitzung aus.** Sie wird geladen; bis sie da ist, gilt das
   * Gerät – und wer auf diesem Gerät schon gewählt hat, sieht seine Ansicht **sofort**, nicht
   * nach einem Aufblitzen der Vorgabe.
   */
  const [gewaehlt, setGewaehlt] = useState<Tagesmodus | null>(() =>
    vomGeraet() ? tagesmodusWaehlen(vomGeraet(), null) : null,
  )

  const setzen = useCallback((neu: Tagesmodus) => {
    setGewaehlt(neu)
    try {
      window.localStorage.setItem(SCHLUESSEL, neu)
    } catch {
      // Nicht speichern können ist kein Grund, die Ansicht nicht umzustellen.
    }
  }, [])

  return [gewaehlt ?? tagesmodusWaehlen(null, vomKonto), setzen]
}
